import React, { useCallback, useState, useRef, useEffect } from 'react';
import {
  ChevronDown,
  ClipboardList,
  FileText,
  Loader2,
  Plus,
  Printer,
} from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type {
  ImportedMedicationProfileResponse,
  MarMedicationResponse,
  OrderResponse,
  PatientMedicationListItem,
  PatientMedicationResponse,
} from '../../services/api';
import ImportedMedicationsNotice from '../imported/ImportedMedicationsNotice';
import ImportedFetchErrorNotice from '../imported/ImportedFetchErrorNotice';
import { IMPORTED_MEDICATIONS_FETCH_FAILED_COPY } from '../imported/importedChartLineCopy';
import { outstandingLines } from '../imported/importedChartLines';
import type { ClinicPhysicianResponse } from '../../services/api/types.gen';
import {
  getMedicationOrderLaunchValidation,
  listAgencyClinicPhysicians,
  printPatientMedications,
} from '../../services/api';
import { usePdfExport } from '../../hooks/usePdfExport';
import type { PdfSource } from '../../hooks/usePdfExport';
import type { AddMedicationMode } from './AddMedicationButton';
import MedicationOrderGenerationFlow from './MedicationOrderGenerationFlow';
import MedicationRecordModal from './MedicationRecordModal';
import MedicationsEmptyState from './MedicationsEmptyState';
import MedicationsErrorState from './MedicationsErrorState';
import MedicationsLoadingState from './MedicationsLoadingState';
import MedicationsTable from './MedicationsTable';

interface MedicationsCardProps {
  medications: PatientMedicationListItem[];
  isLoading: boolean;
  error: string | null;
  patientId: string;
  /**
   * Episode open in the chart. Drives which episode a medication order is
   * created against, and the order-date bounds shown for it; without it the
   * server falls back to the patient's latest active episode, which is not
   * necessarily the one on screen.
   */
  episodeId?: string;
  onRetry: () => void;
  onAddMedication: (mode: AddMedicationMode) => void;
  onEditMedication: (medication: PatientMedicationResponse) => void;
  onDeleteMedication: (medication: PatientMedicationResponse) => void;
  onDiscontinueMedication: (medicationId: string, date: Date) => Promise<void>;
  onMedicationOrderCreated: (order: OrderResponse) => void;
  onDocumentCreated?: () => void;
  /** Visit selector rendered on the left of the action row. */
  visitSelector?: React.ReactNode;
  /** MAR: the selected visit (administration affordances enabled when set). */
  visitId?: string;
  /** Whether the selected visit is still editable (open, not signed/completed). */
  visitEditable?: boolean;
  marByMedicationId?: Map<string, MarMedicationResponse[]>;
  onAddMar?: (medication: PatientMedicationResponse) => void;
  onEditMar?: (entry: MarMedicationResponse) => void;
  onDeleteMar?: (entry: MarMedicationResponse) => void;
  /** Raw Kinnser medication profile for this chart. */
  importedProfile?: ImportedMedicationProfileResponse | null;
  /** Opens the reconciliation screen; absent when the caller cannot reconcile. */
  onOpenImporter?: () => void;
  /** Hides every write: add, edit, delete, discontinue, Generate Order
   *  (External EMR chart lock). */
  readOnly?: boolean;
  /** The imported fetch failed; an error strip takes the section's place. */
  importedFetchFailed?: boolean;
  /** Retries the imported fetch from the error strip. */
  onRetryImported?: () => void;
}

const LAUNCH_VALIDATION_UNAVAILABLE_MESSAGE =
  'Unable to check medication order readiness. Please try again.';
const MEDICATIONS_LOAD_BLOCKER_MESSAGE =
  'Unable to load medications. Please retry.';
const PHYSICIAN_LOAD_BLOCKER_MESSAGE =
  'Unable to load physicians. Please try again.';
const CLINIC_CONTEXT_UNAVAILABLE_MESSAGE =
  'Unable to determine clinic. Please try again.';
const PHYSICIAN_REQUIRED_BLOCKER_MESSAGE =
  'At least one active physician is required to create a medication order.';

interface MedicationOrderLaunchState {
  key: string;
  /**
   * Episode the server resolved and validated against. Create sends this back
   * rather than the `episodeId` prop, so the order lands on exactly the episode
   * whose window produced the order-date bounds — including when the server
   * fell back because no episode was supplied.
   */
  episodeId: string | null;
  defaultClinicPhysicianId: string | null;
  episodeStartDate: string | null;
  episodeEndDate: string | null;
  /**
   * Date the server evaluated the medication-candidate gate at. Seeded into the
   * flow rather than recomputed so the picker cannot disagree with the gate that
   * let the flow open.
   */
  defaultOrderDate: string | null;
}

const MedicationsCard: React.FC<MedicationsCardProps> = ({
  medications,
  isLoading,
  error,
  patientId,
  episodeId,
  onRetry,
  onAddMedication,
  onEditMedication,
  onDeleteMedication,
  onDiscontinueMedication,
  onMedicationOrderCreated,
  onDocumentCreated,
  visitSelector,
  visitId,
  visitEditable = false,
  marByMedicationId,
  onAddMar,
  onEditMar,
  onDeleteMar,
  importedProfile,
  onOpenImporter,
  readOnly = false,
  importedFetchFailed = false,
  onRetryImported,
}) => {
  // Only lines nobody has ruled on yet reach the list. A reconciled line is
  // already represented by the coded medication it became; showing both would
  // read as the patient taking the drug twice.
  const outstandingImportedLines = outstandingLines(importedProfile?.lines);
  const [showMedicationRecordModal, setShowMedicationRecordModal] =
    useState(false);
  const [isGeneratingOrder, setIsGeneratingOrder] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLaunchValidationLoading, setIsLaunchValidationLoading] =
    useState(true);
  const [isPhysiciansLoading, setIsPhysiciansLoading] = useState(false);
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [launchState, setLaunchState] =
    useState<MedicationOrderLaunchState | null>(null);
  const [launchBlockers, setLaunchBlockers] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const activeScopeRef = useRef(`${patientId}|${episodeId ?? ''}`);
  const hasLoadError = Boolean(error);
  const canStartOrderGeneration =
    !isLoading &&
    !isGeneratingOrder &&
    !isLaunchValidationLoading &&
    !isPhysiciansLoading &&
    launchState !== null &&
    launchBlockers.length === 0;
  const { exportPdf, exportingId } = usePdfExport();
  const isPrinting = exportingId === patientId;

  // Unsigned current list for office staff; available even on locked charts.
  const handlePrintMedications = useCallback(() => {
    void exportPdf(
      patientId,
      async (): Promise<PdfSource | null> => {
        const response = await printPatientMedications({
          path: { patient_id: patientId },
        });
        // Thrown so usePdfExport logs the real error behind its generic toast.
        if (response.error) throw response.error;
        if (!response.data) return null;
        return {
          blob: new Blob([response.data as BlobPart], {
            type: 'application/pdf',
          }),
        };
      },
      'medication list'
    );
  }, [exportPdf, patientId]);

  const refreshLaunchValidation = useCallback(() => {
    setLaunchState(null);
    setPhysicians([]);
    setIsPhysiciansLoading(false);

    if (isLoading) {
      setIsLaunchValidationLoading(false);
      setLaunchBlockers([]);
      return undefined;
    }
    if (hasLoadError) {
      setIsLaunchValidationLoading(false);
      setLaunchBlockers([MEDICATIONS_LOAD_BLOCKER_MESSAGE]);
      return undefined;
    }

    let active = true;
    setIsLaunchValidationLoading(true);

    void getMedicationOrderLaunchValidation({
      path: { patient_id: patientId },
      query: episodeId ? { episode_id: episodeId } : undefined,
    })
      .then(response => {
        if (!active) return;

        if (response.error || !response.data) {
          console.error(
            'Medication order launch validation failed',
            response.error
          );
          setLaunchBlockers([LAUNCH_VALIDATION_UNAVAILABLE_MESSAGE]);
          return;
        }

        const blockers = response.data.blockers ?? [];
        if (blockers.length > 0 || !response.data.can_generate) {
          setLaunchBlockers(blockers);
          setLaunchState(null);
          setPhysicians([]);
          return;
        }

        if (!response.data.agency_clinic_id) {
          setLaunchBlockers([CLINIC_CONTEXT_UNAVAILABLE_MESSAGE]);
          setLaunchState(null);
          setPhysicians([]);
          return;
        }

        const key = [
          patientId,
          response.data.episode_id ?? 'no-episode',
          response.data.agency_clinic_id,
        ].join(':');
        setLaunchBlockers([]);
        setLaunchState({
          key,
          episodeId: response.data.episode_id ?? null,
          defaultClinicPhysicianId:
            response.data.episode_clinic_physician_id ?? null,
          episodeStartDate: response.data.episode_start_date ?? null,
          episodeEndDate: response.data.episode_end_date ?? null,
          defaultOrderDate: response.data.default_order_date ?? null,
        });
        setIsPhysiciansLoading(true);

        void listAgencyClinicPhysicians({
          path: { agency_clinic_id: response.data.agency_clinic_id },
        })
          .then(physiciansResponse => {
            if (!active) return;

            if (physiciansResponse.error || !physiciansResponse.data) {
              console.error(
                'Medication order physician load failed',
                physiciansResponse.error
              );
              setPhysicians([]);
              setLaunchBlockers([PHYSICIAN_LOAD_BLOCKER_MESSAGE]);
              return;
            }

            if (physiciansResponse.data.length === 0) {
              setPhysicians([]);
              setLaunchBlockers([PHYSICIAN_REQUIRED_BLOCKER_MESSAGE]);
              return;
            }

            setPhysicians(physiciansResponse.data);
          })
          .catch(errorResponse => {
            console.error(
              'Medication order physician load failed',
              errorResponse
            );
            if (active) {
              setPhysicians([]);
              setLaunchBlockers([PHYSICIAN_LOAD_BLOCKER_MESSAGE]);
            }
          })
          .finally(() => {
            if (active) {
              setIsPhysiciansLoading(false);
            }
          });
      })
      .catch(errorResponse => {
        console.error(
          'Medication order launch validation failed',
          errorResponse
        );
        if (active) {
          setLaunchBlockers([LAUNCH_VALIDATION_UNAVAILABLE_MESSAGE]);
          setLaunchState(null);
          setPhysicians([]);
        }
      })
      .finally(() => {
        if (active) {
          setIsLaunchValidationLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [episodeId, hasLoadError, isLoading, patientId]);

  // Also keyed on episodeId: the refresh below is skipped while the flow is
  // open, so switching episodes mid-flow would otherwise leave the form bound to
  // the previous episode's window while the chart header showed the new one --
  // the same displayed-vs-used mismatch this flow exists to avoid.
  useEffect(() => {
    const scope = `${patientId}|${episodeId ?? ''}`;
    if (activeScopeRef.current === scope) {
      return;
    }

    activeScopeRef.current = scope;
    setIsGeneratingOrder(false);
    setLaunchState(null);
    setPhysicians([]);
    setIsPhysiciansLoading(false);
    setIsLaunchValidationLoading(true);
    setLaunchBlockers([]);
  }, [episodeId, patientId]);

  useEffect(() => {
    if (isGeneratingOrder) {
      return undefined;
    }
    return refreshLaunchValidation();
  }, [isGeneratingOrder, medications, refreshLaunchValidation]);

  const generateOrderButton = (
    <Button
      variant='outline'
      size='sm'
      onClick={() => setIsGeneratingOrder(true)}
      disabled={!canStartOrderGeneration}
    >
      <ClipboardList className='h-4 w-4 mr-2' />
      Generate Order
    </Button>
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSelect = (mode: AddMedicationMode) => {
    setMenuOpen(false);
    onAddMedication(mode);
  };

  return (
    <div>
      <div className='flex flex-wrap items-center justify-between gap-2 mb-4'>
        <div className='flex w-full items-center sm:w-auto'>
          {visitSelector}
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button variant='outline' size='sm' onClick={handlePrintMedications}>
            {isPrinting ? (
              <Loader2 className='h-4 w-4 mr-2 animate-spin' />
            ) : (
              <Printer className='h-4 w-4 mr-2' />
            )}
            Print Medications
          </Button>
          {readOnly ? null : launchBlockers.length > 0 &&
            !isLaunchValidationLoading ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className='inline-flex'
                    data-testid='generate-order-tooltip-trigger'
                    tabIndex={0}
                  >
                    {generateOrderButton}
                  </span>
                </TooltipTrigger>
                <TooltipContent className='max-w-xs p-3'>
                  <div className='space-y-2'>
                    <p className='font-medium text-sm'>
                      Generate Order is unavailable until:
                    </p>
                    <ul className='list-disc pl-4 space-y-1 text-sm'>
                      {launchBlockers.map(blocker => (
                        <li key={blocker}>{blocker}</li>
                      ))}
                    </ul>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            generateOrderButton
          )}
          <Button
            variant='outline'
            size='sm'
            onClick={() => setShowMedicationRecordModal(true)}
            disabled={
              isLoading || isGeneratingOrder || medications.length === 0
            }
          >
            <FileText className='h-4 w-4 mr-2' />
            Med Rec / Snapshot
          </Button>
          <div className={readOnly ? 'hidden' : 'relative'} ref={menuRef}>
            <Button
              size='sm'
              onClick={() => setMenuOpen(prev => !prev)}
              disabled={isGeneratingOrder}
            >
              <Plus className='h-3.5 w-3.5 mr-1.5' />
              Add Medication
              <ChevronDown className='h-3.5 w-3.5 ml-1.5' />
            </Button>
            {menuOpen && (
              <div className='absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-md border bg-popover p-1 shadow-md'>
                {(
                  [
                    { mode: 'standard', label: 'Standard Dosage' },
                    { mode: 'nonstandard', label: 'Nonstandard Dosage' },
                    { mode: 'offmarket', label: 'Off Market / Unlisted' },
                  ] as const
                ).map(item => (
                  <button
                    key={item.mode}
                    onClick={() => handleSelect(item.mode)}
                    className='w-full text-left rounded-sm px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors'
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {!isLoading && !error && importedFetchFailed && onRetryImported && (
        <ImportedFetchErrorNotice
          copy={IMPORTED_MEDICATIONS_FETCH_FAILED_COPY}
          onRetry={onRetryImported}
        />
      )}
      {!isLoading && !error && (
        <ImportedMedicationsNotice
          profile={importedProfile}
          outstandingCount={outstandingImportedLines.length}
          onOpenImporter={onOpenImporter}
        />
      )}
      {isLoading ? (
        <MedicationsLoadingState />
      ) : error ? (
        <MedicationsErrorState error={error} onRetry={onRetry} />
      ) : medications.length === 0 && outstandingImportedLines.length === 0 ? (
        <MedicationsEmptyState />
      ) : isGeneratingOrder ? (
        <MedicationOrderGenerationFlow
          key={launchState?.key}
          patientId={patientId}
          medications={medications}
          physicians={physicians}
          defaultClinicPhysicianId={
            launchState?.defaultClinicPhysicianId ?? null
          }
          episodeId={launchState?.episodeId ?? null}
          episodeStartDate={launchState?.episodeStartDate ?? null}
          episodeEndDate={launchState?.episodeEndDate ?? null}
          defaultOrderDate={launchState?.defaultOrderDate ?? null}
          onCancel={() => setIsGeneratingOrder(false)}
          onCreated={order => {
            setIsGeneratingOrder(false);
            onMedicationOrderCreated(order);
          }}
        />
      ) : (
        <MedicationsTable
          medications={medications}
          importedLines={outstandingImportedLines}
          onEdit={onEditMedication}
          onDelete={onDeleteMedication}
          onDiscontinue={onDiscontinueMedication}
          readOnly={readOnly}
          mar={
            marByMedicationId && onAddMar && onEditMar && onDeleteMar
              ? {
                  visitId,
                  visitEditable,
                  marByMedicationId,
                  onAddMar,
                  onEditMar,
                  onDeleteMar,
                }
              : undefined
          }
        />
      )}
      <MedicationRecordModal
        open={showMedicationRecordModal}
        onOpenChange={setShowMedicationRecordModal}
        patientId={patientId}
        onDocumentCreated={onDocumentCreated}
      />
    </div>
  );
};

export default MedicationsCard;
